import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  LifeBuoy, Loader2, ChevronDown, MessageSquare, Clock, CheckCircle2, AlertCircle, X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { supportApi } from '@/lib/api';
import { format } from 'date-fns';

const PRIORITY_COLORS: Record<string, string> = {
  low:      'text-gray-500 bg-gray-50 border-gray-200',
  medium:   'text-blue-600 bg-blue-50 border-blue-200',
  high:     'text-amber-600 bg-amber-50 border-amber-200',
  critical: 'text-red-600 bg-red-50 border-red-200',
};

const STATUS_OPTS = [
  { value: 'open',        label: 'Open',        icon: AlertCircle,  cls: 'text-amber-600 bg-amber-50' },
  { value: 'in_progress', label: 'In Progress',  icon: Clock,        cls: 'text-blue-600 bg-blue-50' },
  { value: 'resolved',    label: 'Resolved',    icon: CheckCircle2, cls: 'text-green-600 bg-green-50' },
  { value: 'closed',      label: 'Closed',      icon: CheckCircle2, cls: 'text-gray-500 bg-gray-100' },
];

const CATEGORY_LABELS: Record<string, string> = {
  billing:         'Billing',
  technical:       'Technical',
  feature_request: 'Feature Request',
  data_issue:      'Data Issue',
  other:           'Other',
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_OPTS.find(x => x.value === status) ?? STATUS_OPTS[0];
  const Icon = s.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', s.cls)}>
      <Icon size={11}/> {s.label}
    </span>
  );
}

function ReplyModal({ ticket, onClose }: { ticket: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [apiError, setApiError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      admin_reply: ticket.admin_reply ?? '',
      status:      ticket.status,
    },
  });

  const mut = useMutation({
    mutationFn: (data: any) => supportApi.adminReply(ticket.id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['admin-support'] }); onClose(); },
    onError:    (e: any) => setApiError(e.response?.data?.error ?? 'Update failed'),
  });

  const field = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-navy">Reply to Ticket</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={20}/></button>
        </div>

        <div className="px-6 pt-4">
          <p className="text-sm font-medium text-navy">{ticket.subject}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {ticket.hospital_name} · {format(new Date(ticket.created_at), 'dd MMM yyyy')}
          </p>
          <div className="mt-3 bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {ticket.description}
          </div>
        </div>

        <form onSubmit={handleSubmit(d => mut.mutate(d))} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Your Reply *</label>
            <textarea
              {...register('admin_reply', { required: 'Reply is required' })}
              rows={5}
              placeholder="Type your response to the hospital…"
              className={`${field} resize-none`}
            />
            {errors.admin_reply && <p className="text-clinical-danger text-xs mt-1">{errors.admin_reply.message as string}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Update Status</label>
            <select {...register('status')} className={field}>
              {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {apiError && (
            <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
              {apiError}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mut.isPending}
              className="flex-1 py-2.5 rounded-lg bg-orange text-navy font-semibold text-sm
                         hover:bg-orange-dark disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending && <Loader2 size={15} className="animate-spin"/>}
              Send Reply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TicketRow({ ticket }: { ticket: any }) {
  const [expanded, setExpanded] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  return (
    <>
      <tr
        className={clsx(
          'hover:bg-gray-50 cursor-pointer transition-colors',
          expanded && 'bg-gray-50'
        )}
        onClick={() => setExpanded(v => !v)}>
        <td className="px-4 py-3">
          <span className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
            PRIORITY_COLORS[ticket.priority] ?? 'text-gray-500 bg-gray-50 border-gray-200'
          )}>
            {ticket.priority}
          </span>
        </td>
        <td className="px-4 py-3">
          <p className="font-medium text-navy text-sm">{ticket.subject}</p>
          <p className="text-xs text-gray-400">
            {CATEGORY_LABELS[ticket.category] ?? ticket.category} · {ticket.hospital_name}
          </p>
        </td>
        <td className="px-4 py-3"><StatusBadge status={ticket.status}/></td>
        <td className="px-4 py-3 text-xs text-gray-400">
          {format(new Date(ticket.created_at), 'dd MMM yyyy')}
        </td>
        <td className="px-4 py-3">
          <ChevronDown size={14} className={clsx('text-gray-400 transition-transform', expanded && 'rotate-180')}/>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-6 pb-4 pt-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{ticket.description}</p>
            {ticket.admin_reply && (
              <div className="bg-navy/5 rounded-xl p-4 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare size={12} className="text-navy"/>
                  <span className="text-xs font-semibold text-navy">Your reply</span>
                  <span className="text-xs text-gray-400 ml-auto">
                    {format(new Date(ticket.updated_at), 'dd MMM yyyy, hh:mm a')}
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.admin_reply}</p>
              </div>
            )}
            <button
              onClick={e => { e.stopPropagation(); setReplyOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange text-navy text-sm font-semibold
                         hover:bg-orange-dark transition-colors">
              <MessageSquare size={14}/>
              {ticket.admin_reply ? 'Update Reply' : 'Reply'}
            </button>
          </td>
        </tr>
      )}
      {replyOpen && (
        <tr><td colSpan={5} className="p-0">
          <ReplyModal ticket={ticket} onClose={() => setReplyOpen(false)}/>
        </td></tr>
      )}
    </>
  );
}

export default function AdminSupportPage() {
  const [statusFilter,   setStatusFilter]   = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support', statusFilter, priorityFilter],
    queryFn:  () => supportApi.adminList({
      ...(statusFilter   ? { status:   statusFilter }   : {}),
      ...(priorityFilter ? { priority: priorityFilter } : {}),
    }).then(r => r.data),
  });

  const tickets: any[] = data?.tickets ?? [];
  const total: number  = data?.total   ?? 0;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center">
          <LifeBuoy size={20} className="text-orange"/>
        </div>
        <div>
          <h1 className="text-xl font-bold text-navy">Support Tickets</h1>
          <p className="text-xs text-gray-400">{total} total tickets from all hospitals</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30">
            <option value="">All Statuses</option>
            {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <select
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30">
            <option value="">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={24} className="animate-spin text-orange"/>
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center gap-3">
          <LifeBuoy size={32} className="text-gray-200"/>
          <p className="text-sm text-gray-400">No support tickets found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Priority', 'Subject', 'Status', 'Submitted', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.map(t => <TicketRow key={t.id} ticket={t}/>)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
